/**
 * Presentation helpers shared by the desktop app and the web portal.
 *
 * Shared so that a size shown in one reads identically in the other. Two
 * independent implementations drift, and a user comparing the two screens
 * should never have to wonder which is telling the truth.
 */

import type { UnixSeconds } from "./types";

const UNITS = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"] as const;

/** Render a byte count the way a person reads it. */
export function humanBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit + 1 < UNITS.length) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${bytes} ${UNITS[0]}` : `${value.toFixed(1)} ${UNITS[unit]}`;
}

/** Render a Unix-seconds timestamp in the viewer's locale. */
export function humanTime(seconds: UnixSeconds): string {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleString();
}

/** Render a duration given in whole seconds. */
export function humanDuration(seconds: number): string {
  if (seconds <= 0) return "under a second";
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * The proportion of logical data that deduplication removed.
 *
 * Returns `null` rather than zero when there is nothing to compare, so a brand
 * new repository does not display a confident "0% saved".
 */
export function deduplicationRatio(
  logicalBytes: number,
  storedBytes: number,
): number | null {
  if (logicalBytes <= 0) return null;
  return Math.max(0, Math.min(1, 1 - storedBytes / logicalBytes));
}

/** Render a 0–1 proportion as a whole-number percentage. */
export function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/**
 * Shorten a path from the left, keeping the end.
 *
 * The tail is the informative part: which file is involved matters more than
 * which drive it lives on.
 */
export function shortenPath(path: string, limit = 60): string {
  if (path.length <= limit) return path;
  return `…${path.slice(path.length - limit + 1)}`;
}

/** Shorten a long hex identifier for display. */
export function shortId(id: string, length = 12): string {
  return id.length <= length ? id : `${id.slice(0, length)}…`;
}
